import { Dialog, Transition } from "@headlessui/react";
import { Fragment, useEffect, useRef, useState } from "react";
import { Questions } from "../../../Models";

export default function TimeSelectionModal({
  title,
  onConfirm,
  onCancel,
  isModalClosed,
  selectedQuestions,
}: {
  title: string;
  onConfirm: Function;
  onCancel: Function;
  isModalClosed: Function;
  selectedQuestions: Questions[];
}) {
  const [open, setOpen] = useState(true);
  const cancelButtonRef = useRef(null);
  const [selectedTimes, setSelectedTimes] = useState<{ [key: string]: string }>(
    {}
  );
  const [expandQuestion, setExpandQuestion] = useState("");

  useEffect(() => {
    if (!open) isModalClosed();
  }, [open, isModalClosed]);

  const handleTimeChange = (questionId: string, timeId: string) => {
    setSelectedTimes((prev) => ({ ...prev, [questionId]: timeId }));
  };

  const isAllTimesSelected = () => {
    return selectedQuestions.every((q) => selectedTimes[q.id]);
  };

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog
        as="div"
        className="relative z-10"
        initialFocus={cancelButtonRef}
        onClose={() => {
          onCancel();
          setOpen(false);
        }}
      >
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-400 bg-opacity-50 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white p-6 shadow-xl transition-all w-full max-w-2xl">
                <h2 className="text-lg font-bold text-gray-600 mb-4">
                  {title}
                </h2>
                <div className="max-h-96 overflow-y-auto">
                  {selectedQuestions.map((q, index) => (
                    <div
                      key={q.id}
                      className="flex justify-between items-center mb-3"
                    >
                      <div
                        className="cursor-pointer max-w-[70%]"
                        onClick={() =>
                          setExpandQuestion(expandQuestion === q.id ? "" : q.id)
                        }
                      >
                        {expandQuestion === q.id ? (
                          <div className="border p-2 rounded-md">
                            <div>{q.text}</div>
                            <div className="mt-1 text-sm text-gray-500">
                              {q.type.toUpperCase()} | {q.tags}
                            </div>
                          </div>
                        ) : (
                          <div className="border p-2 rounded-md truncate">
                            {q.text.slice(0, 50)}...
                          </div>
                        )}
                      </div>
                      <select
                        className="border p-1 rounded-md w-40"
                        value={selectedTimes[q.id] || ""}
                        onChange={(e) => handleTimeChange(q.id, e.target.value)}
                      >
                        <option value="">Select Time</option>
                        {q.questionTimes.map(
                          (qt) =>
                            !qt.isObsolete && (
                              <option key={qt.id} value={qt.id}>
                                {qt.time} sec | Difficulty {qt.accuracy}%
                              </option>
                            )
                        )}
                      </select>
                    </div>
                  ))}
                </div>
                <div className="mt-4 flex justify-end gap-4">
                  <button
                    onClick={() => setOpen(false)}
                    className="ml-2 px-4 py-2 rounded-md bg-gray-300 hover:bg-gray-400"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => {
                      onConfirm(selectedTimes);
                      setOpen(false);
                    }}
                    disabled={!isAllTimesSelected()}
                    className={`px-4 py-2 rounded-md ${isAllTimesSelected() ? "bg-blue-600 hover:bg-blue-500 text-white" : "bg-gray-400 text-gray-700 cursor-not-allowed"}`}
                  >
                    Confirm
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
